/* istanbul ignore file */
import { HStack, Text, Table, SystemStyleObject } from '@chakra-ui/react';
import React, { useCallback, useContext, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { DataTableContext } from '..';
import { ColumnStyleDefinition, OrderByInput } from '../data-table';
import { getNestedValueFromKeyDotPath } from './utils';
import { OrderByEnum } from '@/constants/sort';
import { Checkbox } from '@/components/ui/checkbox';
import { VisibleFieldsDropdown } from './visible-fields-dropdown';

const getStyleOverride = (styles: ColumnStyleDefinition | undefined) => {
  return styles ? { ...styles.common, ...styles.header } : {};
};

const CurrentSortDirection: React.FC<{ sortKey: string; currentSort: OrderByInput }> = ({ sortKey, currentSort }) => {
  if (!currentSort || currentSort.attribute !== sortKey) {
    return null;
  }
  return currentSort.order === OrderByEnum.Desc ? (
    <ChevronDown width={12} height={12} />
  ) : (
    <ChevronUp width={12} height={12} />
  );
};

export const TableHeaders = () => {
  const { tableProps: props } = useContext(DataTableContext);
  const {
    displayKeyLabelMap,
    visibleKeys,
    styles,
    showVisibleFieldsToggle,
    rowActions,
    sortableKeys,
    pageInfoInput,
    setPageInfoInput,
    centerAlignedKeys,
    rightAlignedKeys,
    variant,
    stickyLeftColumnKeys,
    stickyRightColumnKeys,
    columnStyleOverride,
  } = props;

  const currentOrderBy = pageInfoInput?.orderBy || [];
  const currentSort = currentOrderBy[0];
  const isSortable = sortableKeys && sortableKeys.length > 0;

  const { generalCellStyle, cellStylePerColumn } = columnStyleOverride || {};
  const generalStyleOverride = getStyleOverride(generalCellStyle);

  const handleSortClick = (key: string) => {
    if (!pageInfoInput || !setPageInfoInput || !isSortable) {
      return null;
    }

    const isCurrentKey = currentSort?.attribute === key;
    const newOrderDirection =
      isCurrentKey && currentSort?.order === OrderByEnum.Asc ? OrderByEnum.Desc : OrderByEnum.Asc;

    if (isSortable && sortableKeys?.includes(key)) {
      return setPageInfoInput({
        ...pageInfoInput,
        orderBy: [{ attribute: key, order: newOrderDirection }],
      });
    }
  };

  const headerItem = (visibleKey: string, appliedStyle: SystemStyleObject) => {
    const isCenterAligned = centerAlignedKeys?.includes(visibleKey);
    const isRightAligned = rightAlignedKeys?.includes(visibleKey);

    const columnStyleOverride = getStyleOverride(cellStylePerColumn?.[visibleKey]);

    return (
      <Table.ColumnHeader
        key={visibleKey}
        css={{ ...generalStyleOverride, ...appliedStyle, ...columnStyleOverride }}
        onClick={() => handleSortClick(visibleKey)}
        paddingY={3}
      >
        <HStack
          gap={1}
          css={sortableKeys?.includes(visibleKey) ? { cursor: 'pointer' } : styles.headersText}
          justifyContent={isCenterAligned ? 'center' : isRightAligned ? 'end' : 'baseline'}
        >
          {React.isValidElement(displayKeyLabelMap[visibleKey]) ? (
            displayKeyLabelMap[visibleKey]
          ) : (
            <Text
              textStyle={'captionMedium'}
              fontSize={'xs'}
              color="typography.secondary"
              fontWeight={'medium'}
              lineHeight={1.5}
            >
              {displayKeyLabelMap[visibleKey]}
            </Text>
          )}
          <CurrentSortDirection sortKey={visibleKey} currentSort={currentSort} />
        </HStack>
      </Table.ColumnHeader>
    );
  };

  return (
    <Table.Header>
      <Table.Row backgroundColor={'transparent'}>
        <TableHeaderBulkActions />
        {/* Columns listed on the left defined in `stickyLeftColumnKeys` */}
        {stickyLeftColumnKeys?.map((leftColumnKey) => {
          /* Since Chakra UI has limitations what pseudo classes can be used we had to be creative to create sticky columns in the table */
          const appliedStyle =
            variant === 'fixed-column' ? { ...styles.headers, ...styles.columnFixedFirst } : styles.headers;
          return headerItem(leftColumnKey, appliedStyle);
        })}
        {/* All other columns defined in `visibleKeys` */}
        {visibleKeys?.map((visibleKey) => {
          return headerItem(visibleKey, styles.headers);
        })}
        {/* Columns listed on the right defined in `stickyRightColumnKeys` */}
        {stickyRightColumnKeys?.map((rightColumnKey) => {
          const appliedStyle =
            variant === 'fixed-column' ? { ...styles.headers, ...styles.columnFixedLast } : styles.headers;
          return headerItem(rightColumnKey, appliedStyle);
        })}

        {(showVisibleFieldsToggle || rowActions) && (
          <Table.ColumnHeader w="50px">{showVisibleFieldsToggle && <VisibleFieldsDropdown />}</Table.ColumnHeader>
        )}
      </Table.Row>
    </Table.Header>
  );
};

const TableHeaderBulkActions = () => {
  const { tableProps: props } = useContext(DataTableContext);
  const { styles, bulkActions, selectedRows, setSelectedRows, selectedRowsKey, tableData } = props;

  const allCurrentRowsSelected = useMemo(() => {
    if (!selectedRowsKey || !selectedRows || selectedRows.length === 0) {
      return false;
    }
    return tableData.every((r: Record<string, unknown>) =>
      selectedRows.includes(getNestedValueFromKeyDotPath(r, selectedRowsKey)),
    );
  }, [tableData, selectedRows, selectedRowsKey]);

  const currentPageIds = useMemo(() => {
    if (!selectedRowsKey) {
      return [];
    }
    return tableData.map((r: Record<string, unknown>) => getNestedValueFromKeyDotPath(r, selectedRowsKey));
  }, [tableData, selectedRowsKey]);

  const handleBulkCheckAll = useCallback(() => {
    if (!setSelectedRows || !selectedRowsKey) {
      return;
    }
    const selectedRowsMinusCurrent = [...(selectedRows || []).filter((rowId) => !currentPageIds.includes(rowId))];
    if (allCurrentRowsSelected) {
      return setSelectedRows(selectedRowsMinusCurrent);
    }
    setSelectedRows([
      ...(selectedRowsMinusCurrent || []),
      ...tableData.map((r: Record<string, unknown>) => getNestedValueFromKeyDotPath(r, selectedRowsKey)),
    ]);
  }, [allCurrentRowsSelected, currentPageIds, selectedRows, setSelectedRows, selectedRowsKey, tableData]);

  if (!bulkActions || bulkActions.length === 0) {
    return null;
  }

  return (
    <Table.Cell key="bulkActionsCheckbox" css={styles.bulkSelectCell}>
      <Checkbox onChange={handleBulkCheckAll} checked={allCurrentRowsSelected} />
    </Table.Cell>
  );
};
